import type Phaser from 'phaser';
import { BIOMES_BY_ID } from '@/config/biomes.config';
import { DESTRUCTIBLES_BY_ID } from '@/config/destructibles.config';
import { PETS_BY_ID } from '@/config/pets.config';
import { EGGS_BY_ID } from '@/config/eggs.config';
import {
  AVATAR_SKINS,
  AVATAR_SKINS_BY_ID,
  isAvatarSkinEnabled,
} from '@/config/avatars.config';
import { AMBIENT_FX_PRESETS } from '@/config/ambientFx.config';
import { PARALLAX_PRESETS } from '@/config/parallax.config';
import { CRITTERS_PRESETS } from '@/config/critters.config';
import { PLAZA_COMMS_ICON_KEYS } from '@/config/plazaCommunication.config';
import { getEventByBiomeId, isEventActive } from '@/config/events.config';
import {
  avatarAssetUrl,
  biomeCritterAssetUrl,
  biomeEnvironmentAssetUrl,
  biomeFxAssetUrl,
  destructibleAssetUrl,
  eggAssetUrl,
  petAssetUrl,
} from '@/assets/paths';
import type { PlayerSave } from '@/save/PlayerSave.schema';
import type { PetDef } from '@/domain/types/pet';
import type { TeleportTarget } from '@/game/fx/teleport';

export type AssetDescriptor =
  | { kind: 'image'; key: string; url: string }
  | { kind: 'spritesheet'; key: string; url: string; frameWidth: number; frameHeight: number };

export interface AssetBundle {
  id: string;
  assets: AssetDescriptor[];
}

export interface InitialAssetPlan {
  bundles: AssetBundle[];
  assets: AssetDescriptor[];
}

export interface PreparedAssetScope {
  id: string;
  keys: Set<string>;
  revision: number;
}

type HatchPet = { petDef: PetDef };
export type HatchAssetPayload =
  | (HatchPet & { eggDefId: string })
  | { pets: HatchPet[]; eggDefId: string };

const CORE_SCOPE = 'core';
const PLAYER_SCOPE = 'player';
const HATCH_SCOPE_PREFIX = 'transient:hatch:';
const PLAZA_DYNAMIC_SCOPE_PREFIX = 'transient:plaza-pets:';
const SCENE_SCOPE_PREFIX = 'scene:';
export const RUNTIME_ASSET_LOAD_TIMEOUT_MS = 15_000;

const PLAZA_TEXTURES = [
  'plaza_ground',
  'plaza_stall_empty',
  'plaza_stall_active',
  'plaza_stall_paddock',
  'plaza_stall_expiring',
  'plaza_npc_seller',
  'plaza_decoration_lantern',
  'plaza_decoration_fountain',
  'plaza_bank_building',
  'plaza_stall_egg_basket',
] as const;

const WORLD_UI_TEXTURES = [
  'ui_arrow_tutorial',
  'ui_interact_tap',
  'ui_warning',
  'ui_shell',
] as const;

const image = (key: string, url: string): AssetDescriptor => ({ kind: 'image', key, url });
const sheet = (key: string, url: string): AssetDescriptor => ({
  kind: 'spritesheet',
  key,
  url,
  frameWidth: 256,
  frameHeight: 256,
});

/** Usuwa duplikaty textureKey, zachowując pierwszą deklarację. */
export function uniqueAssets(assets: readonly AssetDescriptor[]): AssetDescriptor[] {
  const seen = new Set<string>();
  return assets.filter((asset) => {
    if (seen.has(asset.key)) return false;
    seen.add(asset.key);
    return true;
  });
}

export function getCoreAssetBundle(): AssetBundle {
  return {
    id: CORE_SCOPE,
    assets: WORLD_UI_TEXTURES.map((key) => image(key, `/ui/${key}.webp`)),
  };
}

export function petAssetDescriptor(pet: PetDef): AssetDescriptor | undefined {
  const biomeId = pet.origin?.biomeId;
  if (!biomeId) return undefined;
  return image(
    pet.art.textureKey,
    petAssetUrl(pet.art.textureKey, biomeId, BIOMES_BY_ID[biomeId]?.eventAssetFolder),
  );
}

export function getPlayerAssetBundle(save: PlayerSave): AssetBundle {
  const assets: AssetDescriptor[] = [];
  const skin = AVATAR_SKINS_BY_ID[save.profile.avatar.skinId];
  if (skin && isAvatarSkinEnabled(skin)) {
    assets.push(image(skin.art.textureKey, avatarAssetUrl(skin.art.textureKey)));
  }

  const inventoryByUid = new Map(save.inventory.map((pet) => [pet.uid, pet]));
  for (const uid of save.equipped) {
    const instance = inventoryByUid.get(uid);
    const descriptor = instance ? petAssetDescriptor(PETS_BY_ID[instance.defId]) : undefined;
    if (descriptor) assets.push(descriptor);
  }

  return { id: PLAYER_SCOPE, assets: uniqueAssets(assets) };
}

/** Pełna paczka jednej mapy, bez petów i jajek niewidocznych w świecie. */
export function getWorldAssetBundle(biomeId: string): AssetBundle {
  const biome = BIOMES_BY_ID[biomeId];
  if (!biome) return { id: `${SCENE_SCOPE_PREFIX}world:${biomeId}`, assets: [] };

  const assets: AssetDescriptor[] = [
    image(
      biome.scene.backgroundTextureKey,
      biomeEnvironmentAssetUrl(
        biome.scene.backgroundTextureKey,
        biome.id,
        biome.eventAssetFolder,
      ),
    ),
  ];

  for (const decoration of biome.decorations ?? []) {
    assets.push(image(
      decoration.textureKey,
      biomeEnvironmentAssetUrl(
        decoration.textureKey,
        biome.id,
        biome.eventAssetFolder,
        decoration.assetFormat,
      ),
    ));
    if (decoration.motion?.kind === 'pageFlip') {
      assets.push(image(
        decoration.motion.pageTextureKey,
        biomeEnvironmentAssetUrl(
          decoration.motion.pageTextureKey,
          biome.id,
          biome.eventAssetFolder,
        ),
      ));
    }
  }

  // Paczka wynika z obiektów faktycznie rozstawionych na mapie, a nie z
  // `destructible.biomeId === biome.id`. Biomy edukacyjne celowo reużywają
  // skrzynki i stosy monet z Łąki, więc ich grafiki należy pobrać z folderu
  // biomu właściciela definicji (`destructible.biomeId`).
  for (const spawn of biome.destructibleSpawns) {
    const destructible = DESTRUCTIBLES_BY_ID[spawn.destructibleId];
    if (!destructible) continue;
    const assetBiome = BIOMES_BY_ID[destructible.biomeId];
    assets.push(image(
      destructible.art.textureKey,
      destructibleAssetUrl(
        destructible.art.textureKey,
        destructible.biomeId,
        assetBiome?.eventAssetFolder,
      ),
    ));
  }

  const fxKey = biome.scene.ambientFx;
  if (fxKey) {
    for (const layer of AMBIENT_FX_PRESETS[fxKey].layers) {
      assets.push(image(
        layer.textureKey,
        biomeFxAssetUrl(layer.textureKey, biome.id, biome.eventAssetFolder),
      ));
    }
  }

  const parallaxKey = biome.scene.parallax;
  if (parallaxKey) {
    for (const layer of PARALLAX_PRESETS[parallaxKey].layers) {
      assets.push(image(
        layer.textureKey,
        biomeEnvironmentAssetUrl(layer.textureKey, biome.id, biome.eventAssetFolder),
      ));
    }
  }

  const crittersKey = biome.scene.critters;
  if (crittersKey) {
    for (const critter of CRITTERS_PRESETS[crittersKey].types) {
      assets.push(image(
        critter.textureKey,
        biomeCritterAssetUrl(critter.textureKey, biome.id, biome.eventAssetFolder),
      ));
    }
  }

  // Eventowa Handlarka w WorldScene współdzieli grafikę NPC Placu. Dawny
  // globalny preload maskował tę zależność; paczka eventowego świata musi ją
  // teraz deklarować jawnie, inaczej po czystym starcie byłby placeholder.
  if (getEventByBiomeId(biome.id)?.dailyReward) {
    assets.push(
      image('plaza_npc_seller', '/biomes/plaza/environment/plaza_npc_seller.webp'),
      sheet('plaza_npc_sheet_a', '/biomes/plaza/environment/plaza_npc_seller-spritesheet.webp'),
      sheet('plaza_npc_sheet_b', '/biomes/plaza/environment/plaza_npc_seller_1.webp'),
    );
  }

  return {
    id: `${SCENE_SCOPE_PREFIX}world:${biome.id}`,
    assets: uniqueAssets(assets),
  };
}

export function getPlazaAssetBundle(): AssetBundle {
  const assets: AssetDescriptor[] = [
    ...PLAZA_TEXTURES.map((key) => image(key, `/biomes/plaza/environment/${key}.webp`)),
    ...PLAZA_COMMS_ICON_KEYS.map((key) => image(key, `/ui/${key}.webp`)),
    sheet('plaza_fountain_sheet', '/biomes/plaza/environment/plaza_decoration_fountain-spritesheet.webp'),
    sheet('plaza_lantern_sheet', '/biomes/plaza/environment/plaza_decoration_lantern-spritesheet_1.webp'),
    sheet('plaza_npc_sheet_a', '/biomes/plaza/environment/plaza_npc_seller-spritesheet.webp'),
    sheet('plaza_npc_sheet_b', '/biomes/plaza/environment/plaza_npc_seller_1.webp'),
  ];

  // Plac pokazuje zdalnych graczy. Cały zestaw avatarów jest małą, ograniczoną
  // paczką i zapobiega tworzeniu prostokątnego fallbacku przed eventem presence.
  for (const skin of AVATAR_SKINS) {
    if (isAvatarSkinEnabled(skin)) {
      assets.push(image(skin.art.textureKey, avatarAssetUrl(skin.art.textureKey)));
    }
  }

  return { id: `${SCENE_SCOPE_PREFIX}plaza`, assets: uniqueAssets(assets) };
}

export function getHalloweenBossAssetBundle(): AssetBundle {
  const base = '/events/capibara_halloween';
  return { id: 'scene:halloween-boss', assets: [
    image('boss_halloween_candy_glutton', `${base}/boss/boss_halloween_candy_glutton.webp`),
    image('boss_halloween_candy_glutton_blink_half', `${base}/boss/boss_halloween_candy_glutton_blink_half.webp`),
    image('boss_halloween_candy_glutton_blink_closed', `${base}/boss/boss_halloween_candy_glutton_blink_closed.webp`),
    image('boss_halloween_candy_glutton_dazed', `${base}/boss/boss_halloween_candy_glutton_dazed.webp`),
    image('boss_halloween_candy_glutton_inhale', `${base}/boss/boss_halloween_candy_glutton_inhale.webp`),
    image('boss_halloween_candy_glutton_blow', `${base}/boss/boss_halloween_candy_glutton_blow.webp`),
    image('boss_halloween_candy_glutton_eat', `${base}/boss/boss_halloween_candy_glutton_eat.webp`),
    image('boss_halloween_candy_glutton_full', `${base}/boss/boss_halloween_candy_glutton_full.webp`),
    image('boss_halloween_rotten_candy', `${base}/boss/boss_halloween_rotten_candy.webp`),
    image('bg_capibara_pumpkin_patch_calm_tile', `${base}/environment/bg_capibara_pumpkin_patch_calm_tile.webp`),
    image('deco_halloween_autumn_grove', `${base}/environment/deco_halloween_autumn_grove.webp`),
    image('deco_halloween_festival_lights', `${base}/environment/deco_halloween_festival_lights.webp`),
    image('ui_candy', '/ui/ui_candy.webp'),
  ] };
}

export function getSanctumAssetBundle(): AssetBundle {
  return {
    id: `${SCENE_SCOPE_PREFIX}sanctum`,
    assets: [
      image('boss_atlantis_guardian', '/biomes/atlantis/boss/boss_atlantis_guardian.webp'),
      image('bg_atlantis_sanctum', '/biomes/atlantis/environment/bg_atlantis_sanctum.webp'),
      image('fx_atlantis_boss_swirl', '/biomes/atlantis/fx/fx_atlantis_boss_swirl.webp'),
      sheet('boss_atlantis_awake_a', '/biomes/atlantis/boss/boss_atlantis_guardian_awake.webp'),
      sheet('boss_atlantis_awake_b', '/biomes/atlantis/boss/boss_atlantis_guardian_awake2.webp'),
      sheet('fx_atlantis_boss_swirl_sheet', '/biomes/atlantis/fx/fx_atlantis_boss_swirl-spritesheet.webp'),
      image('fx_atlantis_boss_emote_anger', '/biomes/atlantis/fx/fx_atlantis_boss_emote_anger.webp'),
      image('fx_atlantis_boss_emote_surprise', '/biomes/atlantis/fx/fx_atlantis_boss_emote_surprise.webp'),
      image('fx_atlantis_boss_emote_resolve', '/biomes/atlantis/fx/fx_atlantis_boss_emote_resolve.webp'),
      image('fx_atlantis_bubble', biomeFxAssetUrl('fx_atlantis_bubble', 'biome-atlantis')),
      image(
        'dest_atlantis_guardian_statue',
        destructibleAssetUrl('dest_atlantis_guardian_statue', 'biome-atlantis'),
      ),
    ],
  };
}

export function getHatchAssetBundle(payload: HatchAssetPayload): AssetBundle {
  const assets: AssetDescriptor[] = [];
  const egg = EGGS_BY_ID[payload.eggDefId];
  if (egg) assets.push(image(egg.art.textureKey, eggAssetUrl(egg.art.textureKey)));

  const pets = 'pets' in payload ? payload.pets : [payload];
  for (const result of pets) {
    const descriptor = petAssetDescriptor(result.petDef);
    if (descriptor) assets.push(descriptor);
  }
  return { id: 'hatch', assets: uniqueAssets(assets) };
}

/** Ten sam resolver zasila preload i WorldScene, włącznie z eventem po terminie. */
export function resolvePlayableBiomeId(save: PlayerSave, now: Date = new Date()): string {
  const requestedId = save.currentBiomeId;
  const biome = BIOMES_BY_ID[requestedId];
  const event = getEventByBiomeId(requestedId);
  const available = biome?.enabled === true || (event != null && isEventActive(event.id, now));
  return biome && available && save.biomesUnlocked.includes(requestedId)
    ? requestedId
    : 'biome-meadow';
}

export function buildInitialAssetPlan(save: PlayerSave): InitialAssetPlan {
  const bundles = [
    getCoreAssetBundle(),
    getPlayerAssetBundle(save),
    getWorldAssetBundle(resolvePlayableBiomeId(save)),
  ];
  return { bundles, assets: uniqueAssets(bundles.flatMap((bundle) => bundle.assets)) };
}

function targetBundle(target: TeleportTarget, save: PlayerSave): AssetBundle {
  if (target === 'HalloweenBossScene') return getHalloweenBossAssetBundle();
  if (target === 'PlazaScene') return getPlazaAssetBundle();
  if (target === 'AtlantisSanctumScene') return getSanctumAssetBundle();
  return getWorldAssetBundle(resolvePlayableBiomeId(save));
}

/**
 * Trzyma jawne zakresy użycia tekstur. Dzięki temu nowa scena może zostać
 * dociągnięta pod zasłoną portalu, a paczka poprzedniego biomu usunięta dopiero
 * po zatrzymaniu jego obiektów Phaser.
 */
export class AssetBundleManager {
  private readonly scopes = new Map<string, Set<string>>();
  private readonly desiredScopeKeys = new Map<string, Set<string>>();
  private readonly scopeRevisions = new Map<string, number>();
  private readonly managedAssets = new Map<string, AssetDescriptor>();
  private readonly runtimeQueues = new WeakMap<object, Promise<void>>();

  queueInitial(scene: Phaser.Scene, save: PlayerSave): InitialAssetPlan {
    const plan = buildInitialAssetPlan(save);
    for (const bundle of plan.bundles) this.setScope(bundle.id, bundle.assets);
    this.queueAssets(scene, plan.assets);
    return plan;
  }

  applyTexturePolicies(
    textures: Phaser.Textures.TextureManager,
    assets: readonly AssetDescriptor[],
  ): void {
    for (const asset of assets) {
      if (asset.key.includes('pixel') && textures.exists(asset.key)) {
        // Phaser.Textures.FilterMode.NEAREST = 1. Type-only import pozwala
        // uruchamiać buildery w testach Node bez inicjalizacji DOM-u Phasera.
        textures.get(asset.key).setFilter(1 as Phaser.Textures.FilterMode);
      }
    }
  }

  async prepareTarget(
    scene: Phaser.Scene,
    target: TeleportTarget,
    save: PlayerSave,
  ): Promise<string> {
    const bundles = [
      getCoreAssetBundle(),
      getPlayerAssetBundle(save),
      targetBundle(target, save),
    ];
    await this.prepareScopes(scene, bundles);
    return bundles[2]!.id;
  }

  ensureCurrentPlayerAssets(
    scene: Phaser.Scene,
    save: PlayerSave,
  ): Promise<PreparedAssetScope> {
    const bundle = getPlayerAssetBundle(save);
    return this.ensureScope(scene, bundle.id, bundle.assets);
  }

  ensureHatchAssets(
    scene: Phaser.Scene,
    payload: HatchAssetPayload,
    runId: string,
  ): Promise<PreparedAssetScope> {
    const bundle = getHatchAssetBundle(payload);
    return this.ensureScope(scene, `${HATCH_SCOPE_PREFIX}${runId}`, bundle.assets);
  }

  ensurePlazaPets(
    scene: Phaser.Scene,
    scopeId: string,
    pets: readonly PetDef[],
  ): Promise<PreparedAssetScope> {
    const assets = pets
      .map(petAssetDescriptor)
      .filter((asset): asset is AssetDescriptor => asset != null);
    return this.ensureScope(scene, `${PLAZA_DYNAMIC_SCOPE_PREFIX}${scopeId}`, assets);
  }

  /**
   * Zawęża zakres z „stare ∪ nowe” do finalnego zestawu. Caller wywołuje to
   * dopiero po podmianie/zniszczeniu sprite'ów starego zestawu.
   */
  finalizePreparedScope(
    prepared: PreparedAssetScope,
    textures: Phaser.Textures.TextureManager,
    animations?: Phaser.Animations.AnimationManager,
  ): boolean {
    if (this.scopeRevisions.get(prepared.id) !== prepared.revision) {
      // Caller wywołuje finalize dopiero PO podmianie sprite'ów. Jeśli w tym
      // czasie inny konsument tego samego zakresu podbił rewizję, jego
      // `desiredScopeKeys` chroni najnowszy snapshot, ale defensywnie zachowujemy
      // też klucze właśnie pokazane przez starego callera. Najnowszy finalize
      // zawęzi zakres ponownie, gdy jego obiekty będą gotowe.
      const retained = new Set(this.scopes.get(prepared.id));
      for (const key of prepared.keys) retained.add(key);
      this.scopes.set(prepared.id, retained);
      this.pruneUnused(textures, animations);
      return false;
    }
    this.scopes.set(prepared.id, new Set(prepared.keys));
    this.desiredScopeKeys.set(prepared.id, new Set(prepared.keys));
    this.pruneUnused(textures, animations);
    return true;
  }

  releasePlazaPets(
    textures: Phaser.Textures.TextureManager,
    animations: Phaser.Animations.AnimationManager,
    scopeId: string,
  ): void {
    this.dropScope(`${PLAZA_DYNAMIC_SCOPE_PREFIX}${scopeId}`);
    this.pruneUnused(textures, animations);
  }

  releaseHatchAssets(
    textures: Phaser.Textures.TextureManager,
    animations: Phaser.Animations.AnimationManager,
    runId: string,
  ): void {
    this.dropScope(`${HATCH_SCOPE_PREFIX}${runId}`);
    this.pruneUnused(textures, animations);
  }

  /**
   * Wywoływane na początku create() sceny docelowej. Phaser przetworzył wtedy
   * wcześniej zakolejkowany stop starej sceny, więc żaden jej obiekt nie będzie
   * już renderowany po usunięciu tekstur.
   */
  activateScene(
    textures: Phaser.Textures.TextureManager,
    animations: Phaser.Animations.AnimationManager,
    activeScope: string,
    save: PlayerSave,
  ): void {
    const player = getPlayerAssetBundle(save);
    this.scopes.set(PLAYER_SCOPE, new Set(player.assets.map((asset) => asset.key)));
    this.desiredScopeKeys.set(PLAYER_SCOPE, new Set(player.assets.map((asset) => asset.key)));

    const desiredScene = this.desiredScopeKeys.get(activeScope);
    if (desiredScene) this.scopes.set(activeScope, new Set(desiredScene));

    const knownScopes = new Set([
      ...this.scopes.keys(),
      ...this.scopeRevisions.keys(),
    ]);
    for (const scope of knownScopes) {
      const oldScene = scope.startsWith(SCENE_SCOPE_PREFIX) && scope !== activeScope;
      // HatchScene jest niezależnym overlayem i sama posiada/releasuje swój
      // zakres. Teleport nie powinien wycinać tekstur hatcha, gdyby oba
      // lifecycle'y kiedykolwiek mogły się nałożyć.
      const transient = scope.startsWith(PLAZA_DYNAMIC_SCOPE_PREFIX);
      if (oldScene || transient) this.dropScope(scope);
    }
    this.pruneUnused(textures, animations);
  }

  /**
   * Usuwa wyłącznie zarządzane, niereferencjonowane tekstury. Animacje globalne
   * muszą zniknąć pierwsze: Phaser przechowuje w nich obiekty Frame niszczone
   * przez TextureManager.remove().
   */
  pruneUnused(
    textures: Phaser.Textures.TextureManager,
    animations?: Phaser.Animations.AnimationManager,
  ): void {
    const retained = this.getRetainedKeys();
    for (const key of this.managedAssets.keys()) {
      if (retained.has(key) || !textures.exists(key)) continue;
      if (animations) {
        for (const animationKey of animations.getAnimsFromTexture(key)) {
          animations.remove(animationKey);
        }
      }
      textures.remove(key);
    }
  }

  /** Widoczne dla testów regresji lifecycle bez uruchamiania renderera. */
  getRetainedKeys(): Set<string> {
    const retained = new Set<string>();
    // `desiredScopeKeys` obowiązuje od chwili rezerwacji, jeszcze przed końcem
    // loadu/stage. Dzięki temu prune z innej ścieżki nie usuwa tekstury, którą
    // równoległy request właśnie pobrał lub zaraz pokaże.
    for (const keys of [...this.scopes.values(), ...this.desiredScopeKeys.values()]) {
      for (const key of keys) retained.add(key);
    }
    return retained;
  }

  private async prepareScopes(
    scene: Phaser.Scene,
    bundles: readonly AssetBundle[],
  ): Promise<PreparedAssetScope[]> {
    const prepared = bundles.map((bundle) => this.reserveScope(bundle.id, bundle.assets));
    await this.enqueueRuntimeLoad(
      scene,
      uniqueAssets(bundles.flatMap((bundle) => bundle.assets)),
    );
    for (const scope of prepared) this.stagePreparedScope(scene, scope);
    return prepared;
  }

  private async ensureScope(
    scene: Phaser.Scene,
    scope: string,
    assets: readonly AssetDescriptor[],
  ): Promise<PreparedAssetScope> {
    const prepared = this.reserveScope(scope, assets);
    await this.enqueueRuntimeLoad(scene, assets);
    this.stagePreparedScope(scene, prepared);
    return prepared;
  }

  private reserveScope(
    scope: string,
    assets: readonly AssetDescriptor[],
  ): PreparedAssetScope {
    const unique = uniqueAssets(assets);
    const revision = (this.scopeRevisions.get(scope) ?? 0) + 1;
    const keys = new Set(unique.map((asset) => asset.key));
    this.scopeRevisions.set(scope, revision);
    this.desiredScopeKeys.set(scope, new Set(keys));
    for (const asset of unique) this.managedAssets.set(asset.key, asset);
    return { id: scope, keys, revision };
  }

  private stagePreparedScope(scene: Phaser.Scene, prepared: PreparedAssetScope): void {
    if (!this.isSceneUsable(scene)) {
      // Shutdown podczas loadu wycofuje wyłącznie własną, nadal aktualną
      // rezerwację. Nowszego requestu tego samego zakresu nie wolno dotknąć.
      if (this.scopeRevisions.get(prepared.id) === prepared.revision) {
        this.desiredScopeKeys.delete(prepared.id);
        this.scopeRevisions.set(prepared.id, prepared.revision + 1);
      }
      return;
    }
    if (this.scopeRevisions.get(prepared.id) !== prepared.revision) return;
    // Stare ∪ nowe pozostaje chronione do atomowego finalize po podmianie obiektów.
    const staged = new Set(this.scopes.get(prepared.id));
    for (const key of prepared.keys) staged.add(key);
    this.scopes.set(prepared.id, staged);
  }

  private setScope(scope: string, assets: readonly AssetDescriptor[]): void {
    const unique = uniqueAssets(assets);
    const keys = new Set(unique.map((asset) => asset.key));
    this.scopes.set(scope, keys);
    this.desiredScopeKeys.set(scope, new Set(keys));
    this.scopeRevisions.set(scope, (this.scopeRevisions.get(scope) ?? 0) + 1);
    for (const asset of unique) this.managedAssets.set(asset.key, asset);
  }

  private dropScope(scope: string): void {
    this.scopeRevisions.set(scope, (this.scopeRevisions.get(scope) ?? 0) + 1);
    this.desiredScopeKeys.delete(scope);
    this.scopes.delete(scope);
  }

  private queueAssets(scene: Phaser.Scene, assets: readonly AssetDescriptor[]): void {
    for (const asset of uniqueAssets(assets)) {
      this.managedAssets.set(asset.key, asset);
      if (scene.textures.exists(asset.key)) continue;
      if (asset.kind === 'image') {
        scene.load.image(asset.key, asset.url);
      } else {
        scene.load.spritesheet(asset.key, asset.url, {
          frameWidth: asset.frameWidth,
          frameHeight: asset.frameHeight,
        });
      }
    }
  }

  private enqueueRuntimeLoad(
    scene: Phaser.Scene,
    assets: readonly AssetDescriptor[],
  ): Promise<void> {
    for (const asset of assets) this.managedAssets.set(asset.key, asset);
    const loader = scene.load as unknown as object;
    const previous = this.runtimeQueues.get(loader) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(() => this.loadNow(scene, assets));
    this.runtimeQueues.set(loader, next);
    return next;
  }

  private async loadNow(
    scene: Phaser.Scene,
    assets: readonly AssetDescriptor[],
  ): Promise<void> {
    if (!this.isSceneUsable(scene)) return;
    if (scene.load.isLoading()) {
      const existingLoadFinished = await this.waitForLoader(scene, false);
      if (!existingLoadFinished || !this.isSceneUsable(scene)) return;
    }

    const missing = uniqueAssets(assets).filter((asset) => !scene.textures.exists(asset.key));
    if (missing.length === 0) {
      this.applyTexturePolicies(scene.textures, assets);
      return;
    }
    this.queueAssets(scene, missing);
    await this.waitForLoader(scene, true);
    this.applyTexturePolicies(scene.textures, missing);
  }

  private waitForLoader(scene: Phaser.Scene, start: boolean): Promise<boolean> {
    if (!this.isSceneUsable(scene)) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      let settled = false;
      let timeout: ReturnType<typeof setTimeout> | undefined;
      const onError = (file: Phaser.Loader.File): void => {
        console.warn(
          `[AssetBundleManager] Nie udało się załadować: ${file.key} (${file.url}).`,
        );
      };
      const cleanup = (): void => {
        if (timeout) clearTimeout(timeout);
        scene.load.off('loaderror', onError);
        scene.load.off('complete', onComplete);
        scene.events.off('shutdown', onAbort);
        scene.events.off('destroy', onAbort);
      };
      const finish = (completed: boolean): void => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(completed);
      };
      const onComplete = (): void => finish(true);
      const onAbort = (): void => finish(false);

      scene.load.on('loaderror', onError);
      scene.load.once('complete', onComplete);
      scene.events.once('shutdown', onAbort);
      scene.events.once('destroy', onAbort);
      timeout = setTimeout(() => {
        console.warn(
          `[AssetBundleManager] Timeout ${RUNTIME_ASSET_LOAD_TIMEOUT_MS} ms; ` +
          'kontynuuję z fallbackami.',
        );
        finish(false);
      }, RUNTIME_ASSET_LOAD_TIMEOUT_MS);

      if (start) {
        try {
          scene.load.start();
        } catch (err) {
          console.warn('[AssetBundleManager] Loader nie wystartował:', err);
          finish(false);
        }
      }
    });
  }

  private isSceneUsable(scene: Phaser.Scene): boolean {
    return scene.sys.isActive() || scene.sys.isPaused() || scene.sys.isSleeping();
  }
}

export const assetBundleManager = new AssetBundleManager();
